import React, { useState } from 'react';
import {
  Container,
  Typography,
  TextField,
  MenuItem,
  Button,
  Paper,
  Alert,
  Box,
  Grid,
  Stack,
} from '@mui/material';
import LocalPhoneIcon from '@mui/icons-material/LocalPhone';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import LocationOnIcon from '@mui/icons-material/LocationOn';

import { submitContact } from '../../services/contactService';
import LandingNavbar from './LandingPageNavBar';

const SUBJECT_OPTIONS = [
  { value: 'account_management', label: 'Account Management' },
  { value: 'pricing_plans', label: 'Pricing and Plans' },
  { value: 'installation_setup', label: 'Installation Setup' },
  { value: 'technical_support', label: 'Technical Support' },
  { value: 'business_inquiry', label: 'Business Inquiry' },
  { value: 'general_inquiry', label: 'General Inquiry' },
];

const defaultForm = {
  name: '',
  email: '',
  phone: '',
  organization: '',
  subject: 'business_inquiry',
  message: '',
};

const infoCards = [
  { icon: <LocationOnIcon fontSize="large" />, label: 'Head Office', value: '63 Knowl Piece, Hitchin, SG4 0TY' },
  { icon: <LocalPhoneIcon fontSize="large" />, label: 'Call Us', value: '+44 (0)1462 123 456' },
  { icon: <MailOutlineIcon fontSize="large" />, label: 'Email', value: 'info@delightsupportedliving.co.uk' },
];

const ContactPage = () => {
  const [form, setForm] = useState(defaultForm);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      await submitContact({ ...form, source: 'public-form' });
      setSuccess('Thanks for reaching out. Our team will contact you shortly.');
      setForm(defaultForm);
    } catch (err) {
      setError('Unable to send your message right now. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <LandingNavbar />
      <Box sx={{ backgroundColor: '#8E51FF', minHeight: '100vh', pt: 14, pb: 8, color: '#fff' }}>
        <Container maxWidth="md">
          <Box textAlign="center" mb={6}>
            <Typography variant="h3" fontWeight={700}>
              Talk to our care liaison team
            </Typography>
            <Typography variant="body1" sx={{ mt: 2, opacity: 0.9 }}>
              Tell us about your organisation or loved one-we will build a personalised plan and arrange a clinical consultation.
            </Typography>
          </Box>

          <Paper sx={{ p: 4, borderRadius: 4, boxShadow: '0 20px 45px rgba(0,0,0,0.35)' }}>
            <Typography variant="h5" fontWeight={700} gutterBottom color="text.primary">
              Send us a message
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Our response team aims to reply within the same business day.
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            {success && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {success}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit} display="flex" flexDirection="column" gap={2}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField label="Name" name="name" value={form.name} onChange={handleChange} required fullWidth />
                <TextField label="Email" name="email" type="email" value={form.email} onChange={handleChange} required fullWidth />
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField label="Phone" name="phone" value={form.phone} onChange={handleChange} fullWidth />
                <TextField label="Organisation" name="organization" value={form.organization} onChange={handleChange} fullWidth />
              </Stack>
              <TextField select label="Subject" name="subject" value={form.subject} onChange={handleChange} fullWidth>
                {SUBJECT_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Message"
                name="message"
                value={form.message}
                onChange={handleChange}
                multiline
                minRows={4}
                required
                fullWidth
              />
              <Button type="submit" variant="contained" color="primary" disabled={loading} size="large">
                {loading ? 'Sending...' : 'Submit enquiry'}
              </Button>
            </Box>
          </Paper>

          <Grid container spacing={3} mt={6} justifyContent="center">
            {infoCards.map((info) => (
              <Grid item xs={12} sm={4} key={info.label} display="flex" justifyContent="center">
                <Paper
                  sx={{
                    p: 2,
                    textAlign: 'center',
                    borderRadius: 3,
                    backgroundColor: 'rgba(255,255,255,0.12)',
                    boxShadow: '0 10px 30px rgba(0,0,0,0.25)',
                    color: '#fff',
                    maxWidth: 360,
                    minWidth: 320,
                    width: '100%',
                  }}
                >
                  <Box sx={{ mb: 1 }}>{info.icon}</Box>
                  <Typography variant="subtitle2" sx={{ textTransform: 'uppercase', opacity: 0.7 }}>
                    {info.label}
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {info.value}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    </>
  );
};

export default ContactPage;
